/**
 * cryptoIntegrityCheck.js
 *
 * Startup sanity check for the Web Crypto CSPRNG.
 *
 * Rationale (GS-A_4367 / BSI-TR-03116-1 §3.8 G1 remediation):
 * The product relies on crypto.getRandomValues for all cryptographic RNG
 * calls (recovery keys, AES-GCM IVs, PBKDF2 salts, SSO CSRF nonces).
 * This module verifies the API is present and producing non-trivial output
 * before the app mounts, catching obvious tampering or broken environments
 * (e.g. all-zero or constant-value output from a shimmed window.crypto).
 *
 * This is not a full statistical RNG test — it is a fail-fast guard that
 * catches the most likely failure modes (absent API, all-zero buffer,
 * constant-value buffer).  The Content-Security-Policy (script-src 'self')
 * is the primary defence against extension-based shimming; this check is
 * the secondary in-app verification.
 *
 * See: docs/audit/GS-A_4367_Random_Number_Generation.md §7
 */

import logger from './logger';

const SAMPLE_BYTES = 64;

/**
 * Run the Web Crypto integrity check.
 *
 * @returns {{ ok: boolean, reason?: string }}
 *   ok     - true if the CSPRNG appears functional.
 *   reason - human-readable failure description (only present when ok === false).
 */
export function checkCryptoIntegrity() {
  // 1. API presence
  if (typeof window === 'undefined' || typeof window.crypto?.getRandomValues !== 'function') {
    const reason = 'Web Crypto API (crypto.getRandomValues) is not available in this browser.';
    logger.error('CryptoIntegrityCheck:', reason);
    return { ok: false, reason };
  }

  // 2. Produce a sample buffer
  let buf;
  try {
    buf = new Uint8Array(SAMPLE_BYTES);
    window.crypto.getRandomValues(buf);
  } catch (err) {
    const reason = `crypto.getRandomValues threw an error: ${err.message}`;
    logger.error('CryptoIntegrityCheck:', reason);
    return { ok: false, reason };
  }

  // 3. All-zero check — the most common symptom of a broken/mocked RNG
  if (buf.every(b => b === 0)) {
    const reason = 'RNG sanity check failed: crypto.getRandomValues returned an all-zero buffer.';
    logger.error('CryptoIntegrityCheck:', reason);
    return { ok: false, reason };
  }

  // 4. Constant-value check — catches e.g. all-0xFF or any uniform fill
  if (buf.every(b => b === buf[0])) {
    const reason = `RNG sanity check failed: crypto.getRandomValues returned a constant-value buffer (0x${buf[0].toString(16).padStart(2, '0')}).`;
    logger.error('CryptoIntegrityCheck:', reason);
    return { ok: false, reason };
  }

  logger.info('CryptoIntegrityCheck: CSPRNG appears functional.');
  return { ok: true };
}
